'use client';

import { useEffect, useMemo, type ReactNode } from 'react';
import { PortableText, type PortableTextComponents } from '@portabletext/react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';
import { CTAButton } from '@/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/ui/dialog';
import type { IPromoBanner } from '@/sanity/lib/definitions';
import { resolveI18nRichtext } from '@/sanity/globals/I18n';
import { usePromoPopupOnce } from '@/hooks/usePromoPopupOnce';
import { useBannerCountdown } from '@/hooks/useBannerCountdown';
import { getCountdownLabels, pad2 } from '@/lib/bannerCountdown';

/**
 * Promo popup, "New UI 2026" V1 design.
 *
 * Built 1:1 from Figma (IQ Capital | New UI 2026):
 *   desktop 7637:17638 (971x552 glass frame around a 959x538 card)
 *   mobile  8632:8940  (400x582 frame around a 388x570 card, 435 design width)
 *
 * Sizes are the Figma px divided by 8, which is this project's rem scale (see
 * app/globals.scss), so the popup renders at its design size on a 1920
 * viewport. Blur radii stay in px — they are an optical effect, not layout.
 * `lg:` is 993px here (tailwind.config.ts), so the mobile frame is the base
 * and the desktop frame is the `lg:` override.
 *
 * Behaviour is identical to PromoBannerDialogClassic: the scroll-triggered,
 * once-a-day open state comes from usePromoPopupOnce, the copy from the
 * document's i18n fallback chain, and the CTA stays a CTAButton so its
 * CTAWrapper keeps resolving link/popup/anchor actions and so the affiliate
 * pages can keep rewriting `data.button.cta.link`.
 *
 * The rotated "80% off" Bebas marquee of the Figma frame (7625:11662) is
 * deliberately not reproduced: it sits at top:-1144px, entirely outside the
 * clipped card.
 */

interface PromoBannerDialogV1Props {
  data: IPromoBanner;
  language?: string;
  /** Preview harnesses: open immediately, ignore the once-a-day stamp. */
  forceOpen?: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Rich text                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Local PortableText components — richTextComponent is wrong on this card: its
 * highlight marks paint light-green backgrounds meant for light sections.
 *
 * Figma 7836:7070 sets the headline in Geist Medium white and the opening
 * clause ("80% OFF + Buy 3") in Blacker Display Italic #dcf8f0, so every
 * serif/highlight mark the editors can reach maps onto that one italic serif
 * span. Blacker Display only ships 300/400 (normal + italic), and Figma asks
 * for its Regular Italic, so `font-normal italic` is the exact weight — no
 * faux bold needed here.
 */
const serifMark = ({ children }: { children?: ReactNode }) => (
  <span className="font-blacker font-normal italic text-[#dcf8f0]">
    {children}
  </span>
);

const promoTextComponents: PortableTextComponents = {
  block: {
    // 48px/8 = 6rem desktop, 32px/8 = 4rem mobile; tracking -1.44px/-0.96px.
    // The 522px (65.25rem) headline is wider than the 474px column — that is
    // what Figma 7836:7070 does, the overflow is symmetric and intended.
    h1: ({ children }) => <PromoHeadline>{children}</PromoHeadline>,
    h2: ({ children }) => <PromoHeadline>{children}</PromoHeadline>,
    h3: ({ children }) => <PromoHeadline>{children}</PromoHeadline>,
    h4: ({ children }) => <PromoHeadline>{children}</PromoHeadline>,
    h5: ({ children }) => <PromoHeadline>{children}</PromoHeadline>,
    h6: ({ children }) => <PromoHeadline>{children}</PromoHeadline>,
    // 7836:7071 "At zero, full price returns." — 18px/8 = 2.25rem, -0.54px.
    normal: ({ children }) => (
      <p className="mb-0 text-center font-geist text-[2.25rem] font-normal leading-normal tracking-[-0.0675rem] text-white opacity-70">
        {children}
      </p>
    ),
  },
  marks: {
    serif: serifMark,
    em: serifMark,
    lightGreenHighlight: serifMark,
    lightGreenSerif: serifMark,
    darkGreenSerif: serifMark,
    greenSerifHighlight: serifMark,
    strong: ({ children }) => (
      <span className="font-semibold">{children}</span>
    ),
    underline: ({ children }) => <u className="underline">{children}</u>,
  },
  // Any other mark the editors leave on the text renders untouched rather
  // than dragging a light-theme style onto the dark card.
  unknownMark: ({ children }) => <>{children}</>,
};

function PromoHeadline({ children }: { children?: ReactNode }) {
  return (
    <p className="mb-0 w-[37.75rem] max-w-none text-center font-geist text-[4rem] font-medium leading-[0.98] tracking-[-0.12rem] text-white lg:w-[65.25rem] lg:text-[6rem] lg:tracking-[-0.18rem]">
      {children}
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/*  Decorative layers                                                          */
/* -------------------------------------------------------------------------- */

/**
 * One IQ coin (7625:11679 / 7625:11680 desktop, 8632:9007 / 8632:9010 mobile).
 * Figma rotates the artwork 20.88deg inside a larger bounding box, and the
 * image itself is an object-cover crop of a taller asset (117.67% tall, pulled
 * up 6.98%).
 */
function Coin({ box, art }: { box: string; art: string }) {
  return (
    <div
      className={cn(
        'absolute flex -translate-x-1/2 items-center justify-center',
        box,
      )}
    >
      <div className="flex-none rotate-[20.88deg]">
        <div className={cn('relative overflow-hidden', art)}>
          {/* eslint-disable-next-line @next/next/no-img-element -- decorative
              plate; next/image would add no value to a fixed-size ornament. */}
          <img
            src="/promo-v1/coin.webp"
            alt=""
            className="absolute left-0 top-[-6.98%] h-[117.67%] w-full max-w-none object-cover"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * One blurred light wedge (7625:11671..73 desktop, 8632:8949..51 mobile).
 * The exported SVG carries the blur bleed, which is why the <img> sits at a
 * negative inset instead of filling the box.
 */
function LightWedge({
  src,
  box,
  bleed,
  className,
}: {
  src: string;
  box: string;
  bleed: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'absolute h-[120.24838rem] -translate-x-1/2 opacity-[0.07]',
        box,
        className,
      )}
    >
      <div className={cn('absolute', bleed)}>
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
        <img src={src} alt="" className="block h-full w-full max-w-none" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Countdown                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * 7652:17910 (desktop) / 8632:8966 (mobile).
 *
 * Figma trims both text boxes to their cap height ([text-box-trim:trim-both] +
 * [text-box-edge:cap_alphabetic]) and then states a 116.03px leading that the
 * trim throws away, so the padding alone sets the 76x84px box. `leading-[0.72]`
 * is that cap-height trim expressed as a line-height; `leading-none` would
 * make the digit line box a full 44px and push the unit ~19px past the design.
 *
 * The digits get a fixed min-width and tabular figures so the boxes keep their
 * width while the clock runs.
 */
function CountdownUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-[1.36975rem] rounded-[1.020625rem] border-[0.170125rem] border-white/10 bg-white/[0.06] px-[1.174125rem] py-[1.36975rem] lg:gap-[1.75rem] lg:rounded-[1.303875rem] lg:border-[0.21725rem] lg:px-[1.5rem] lg:py-[1.75rem]">
      {/* 32px/8 = 4rem mobile, 44px/8 = 5.5rem desktop */}
      <p className="mb-0 min-w-[4.75rem] text-center font-blacker text-[4rem] font-normal leading-[0.72] tracking-[-0.0978rem] text-white tabular-nums lg:min-w-[6.5rem] lg:text-[5.5rem] lg:tracking-[-0.125rem]">
        {pad2(value)}
      </p>
      {/* 14px/8 = 1.75rem, tracking -0.42px/8 */}
      <p className="mb-0 whitespace-nowrap text-center font-geist text-[1.75rem] font-normal uppercase leading-[0.72] tracking-[-0.0525rem] text-white opacity-60">
        {label}
      </p>
    </div>
  );
}

/** 7652:17913 — Figma exports the ":" as a 6.807x27.517px vector. */
function CountdownSeparator() {
  return (
    <span
      aria-hidden
      className="self-center font-blacker text-[2.69225rem] font-normal leading-none text-white lg:text-[3.439625rem]"
    >
      :
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function PromoBannerDialogV1({
  data,
  language,
  forceOpen,
}: PromoBannerDialogV1Props) {
  const [open, setOpen] = usePromoPopupOnce(forceOpen);
  const timeLeft = useBannerCountdown(data);

  // Same rule as the classic popup: the translation wins on non-default
  // languages and falls back to the stored copy when that language is empty.
  const text = resolveI18nRichtext(data.text, data.textI18n, language);

  // Used as an object key, so it has to survive draft mode's stega characters.
  const labels = getCountdownLabels(stegaClean(language) || undefined);

  // The live documents end on an empty "normal" block, which would otherwise
  // add a phantom row to the gapped content column.
  const blocks = useMemo(() => {
    if (!text) return undefined;
    const filtered = text.filter((block) => {
      const candidate = block as {
        _type?: string;
        children?: { text?: string }[];
      };
      if (candidate._type !== 'block') return true;
      return (candidate.children ?? []).some(
        (child) => (child?.text ?? '').trim().length > 0,
      );
    });
    return filtered.length ? filtered : undefined;
  }, [text]);

  useEffect(() => {
    if (open) document.body.style.overflow = 'unset';
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* 400px/8 = 50rem mobile frame, 971px/8 = 121.375rem desktop frame */}
      {/* `!outline-none`: Radix focuses this container on open (auto-focus is
          prevented in the base) and Chrome draws the `outline-style: auto`
          focus ring even at the base's zero width — a stroke around the
          frame that is not in the design. */}
      <DialogContent
        hideCloseButton
        className="!w-[min(50rem,calc(100%-2rem))] !border-0 !bg-transparent !p-0 !outline-none !duration-700 lg:!w-[121.375rem]"
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Promo</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>

        {/* Glass frame — 6px padding, 11px radius, 1px hairline. The desktop
            frame is a touch brighter than the mobile one (0.19 vs 0.1). */}
        <div className="flex flex-col items-start rounded-[1.375rem] border border-white/10 bg-[rgba(149,149,149,0.1)] p-[0.75rem] lg:border-white/[0.19]">
          {/* Inner card — 570px/8 mobile, 538px/8 desktop. `isolate` keeps the
              blend modes below compositing against this card only. */}
          <div className="relative isolate h-[71.25rem] w-full overflow-hidden rounded-[0.75rem] bg-[#001b14] lg:h-[67.25rem] lg:rounded-[1rem]">
            {/* ---------------- Background stack (7625:11660) ------------- */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden"
              style={{
                backgroundImage:
                  'linear-gradient(180deg, rgba(0, 0, 0, 0.6) 27.116%, rgba(0, 0, 0, 0) 59.929%)',
              }}
            >
              {/* 1. Halftone noise plate (7637:16373 / 8632:8947) */}
              <div className="absolute inset-0 overflow-hidden opacity-10 mix-blend-exclusion">
                {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
                <img
                  src="/promo-v1/noise.webp"
                  alt=""
                  className="absolute left-0 top-0 h-full w-full max-w-none object-cover"
                />
              </div>

              {/* 2. Baroque ceiling photo (7625:11661 / 8632:8945).
                  Desktop: 1186x790.874 at left calc(50%+39px), top -111px.
                  Mobile: Figma spans it left -74.97% .. right -88.44% behind an
                  alpha mask; approximated here as one wide centred box
                  (263.41% of the card, same 1712:1141.63 aspect) without the
                  mask — at 11% color-dodge the mask edge is not readable. */}
              <div className="absolute left-1/2 top-[-3.605rem] h-[85.175rem] w-[263.41%] -translate-x-1/2 opacity-[0.11] mix-blend-color-dodge lg:left-[calc(50%+4.875rem)] lg:top-[-13.875rem] lg:h-[98.85925rem] lg:w-[148.25rem]">
                {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
                <img
                  src="/promo-v1/texture.webp"
                  alt=""
                  className="absolute inset-0 h-full w-full max-w-none object-cover"
                />
              </div>

              {/* 3. Light wedges (7625:11670 "light" / 8632:8948).
                  Desktop centres on 50%, mobile on 50% - 17px; the wedges also
                  start 52px lower on mobile (-403.78 vs -455.78 / -466 vs
                  -518). All three are 961.987px tall at 7% opacity. */}
              <LightWedge
                src="/promo-v1/light-1.svg"
                box="left-[calc(50%-2.125rem)] top-[-50.4725rem] w-[52.5rem] lg:left-1/2 lg:top-[-56.9725rem]"
                bleed="inset-[-3.64%_-8.33%]"
              />
              <LightWedge
                src="/promo-v1/light-2.svg"
                box="left-[calc(50%-2.125rem)] top-[-58.25rem] w-[34.364875rem] lg:left-1/2 lg:top-[-64.75rem]"
                bleed="inset-[-5.77%_-20.18%]"
                className="mix-blend-plus-lighter"
              />
              <LightWedge
                src="/promo-v1/light-3.svg"
                box="left-[calc(50%-2.125rem)] top-[-58.25rem] w-[11.47225rem] lg:left-1/2 lg:top-[-64.75rem]"
                bleed="inset-[-5.77%_-60.45%]"
                className="mix-blend-plus-lighter"
              />

              {/* 4. Blurred green glows (7625:11674/75 desktop, 8632:8942
                  mobile). Mobile has one unrotated 184x184 glow, desktop two
                  266x358 ones rotated 34.73deg inside 422.578x445.765 boxes. */}
              <div className="absolute left-[12.875rem] top-[8.125rem] h-[23rem] w-[23rem] mix-blend-lighten lg:hidden">
                <div className="absolute inset-[-30.71%]">
                  {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
                  <img
                    src="/promo-v1/ellipse.svg"
                    alt=""
                    className="block h-full w-full max-w-none"
                  />
                </div>
              </div>
              <div className="absolute left-[88.125rem] top-[22.125rem] hidden h-[55.720625rem] w-[52.82225rem] items-center justify-center mix-blend-lighten lg:flex">
                <div className="flex-none rotate-[34.73deg]">
                  <div className="relative h-[44.75rem] w-[33.25rem]">
                    <div className="absolute inset-[-22.82%_-30.71%]">
                      {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
                      <img
                        src="/promo-v1/ellipse.svg"
                        alt=""
                        className="block h-full w-full max-w-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute left-[-17rem] top-[-13.875rem] hidden h-[55.720625rem] w-[52.82225rem] items-center justify-center mix-blend-lighten lg:flex">
                <div className="flex-none rotate-[34.73deg]">
                  <div className="relative h-[44.75rem] w-[33.25rem]">
                    <div className="absolute inset-[-22.82%_-30.71%]">
                      {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
                      <img
                        src="/promo-v1/ellipse.svg"
                        alt=""
                        className="block h-full w-full max-w-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Blurred frame with the oval hole (7625:11676 / 8632:8952).
                  875x1057.836; the 0.7 opacity is baked into the asset. */}
              <div className="absolute left-1/2 top-[-13.5rem] h-[132.2295rem] w-[109.375rem] -translate-x-1/2 lg:left-[-30.25rem] lg:top-[-31.875rem] lg:translate-x-0">
                <div className="absolute inset-[-21.55%_-26.05%]">
                  {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
                  <img
                    src="/promo-v1/subtract.svg"
                    alt=""
                    className="block h-full w-full max-w-none"
                  />
                </div>
              </div>

              {/* 6. IQ coins. Mobile: small top-left + larger bottom-right.
                  Desktop: small top-left + big bottom-right, both clipped. */}
              <Coin
                box="left-[calc(50%-22.24375rem)] top-[-7.5rem] h-[27.493rem] w-[26.76325rem] lg:hidden"
                art="h-[21.64825rem] w-[20.38525rem]"
              />
              <Coin
                box="left-[calc(50%+23.55875rem)] top-[46.25rem] h-[38.689125rem] w-[37.662125rem] lg:hidden"
                art="h-[30.464125rem] w-[28.686875rem]"
              />
              <Coin
                box="hidden left-[calc(50%-53.7025rem)] top-[-13.875rem] h-[45.555125rem] w-[44.346rem] lg:flex"
                art="h-[35.8705rem] w-[33.77775rem]"
              />
              <Coin
                box="hidden left-[calc(50%+66.29625rem)] top-[15.125rem] h-[83.56125rem] w-[81.343375rem] lg:flex"
                art="h-[65.79675rem] w-[61.95825rem]"
              />

              {/* 7. Edge fades (7625:11681 right, 7625:11690 left; 8632:9017 /
                  8632:8958 on mobile). 44px/154px right, 83px/162px left. */}
              <div className="absolute left-0 top-0 h-full w-[10.375rem] bg-gradient-to-r from-[rgba(0,16,11,0.05)] to-[rgba(0,16,11,0)] lg:w-[20.25rem]" />
              <div className="absolute right-0 top-0 h-full w-[5.5rem] bg-gradient-to-l from-[rgba(0,16,11,0.05)] to-[rgba(0,16,11,0)] lg:w-[19.25rem]" />
            </div>

            {/* ---------------- Content column (7625:11682 / 8632:8959) --- */}
            <div className="absolute left-1/2 top-1/2 flex w-[44.375rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[3rem] lg:w-[59.25rem] lg:gap-[3.875rem]">
              {blocks && (
                // 12px/8 = 1.5rem between the headline and the subline.
                <div className="flex w-full flex-col items-center gap-[1.5rem] [word-break:break-word]">
                  <PortableText value={blocks} components={promoTextComponents} />
                </div>
              )}

              {data.button && (
                // Figma 7625:11688 / 8632:9011: white, 46px tall, 2px radius,
                // 446px wide on desktop and full width on mobile, label Geist
                // SemiBold 18px (14px mobile), capitalised. CTAButton stays in
                // place so its CTAWrapper keeps resolving link/popup/anchor
                // actions and the affiliate pages keep rewriting cta.link; the
                // Figma styling is layered on through scss/global/button.css's
                // hooks (.button, .button-text) with `!` to beat @apply.
                <CTAButton
                  data={data.button}
                  language={language}
                  overrideVariant="white"
                  className={cn(
                    '!w-full sm:!w-full lg:!w-[55.75rem]',
                    '[&_.button]:!min-h-[5.75rem] [&_.button]:!rounded-[0.25rem] [&_.button]:!px-[3.5rem]',
                    '[&_.button]:!text-[1.75rem] lg:[&_.button]:!text-[2.25rem]',
                    '[&_.button]:!font-semibold',
                    '[&_.button]:!tracking-[-0.035rem] lg:[&_.button]:!tracking-[-0.045rem]',
                    '[&_.button-text]:capitalize [&_.button-text-hover]:capitalize',
                  )}
                />
              )}

              {/* 7652:17909 / 8632:8965 — only inside the countdown window;
                  useBannerCountdown returns null before mount, before the
                  start date and after the end date. */}
              {timeLeft && (
                <div className="flex items-center gap-[0.680375rem] lg:gap-[0.86925rem]">
                  <CountdownUnit value={timeLeft.days} label={labels[0]} />
                  <CountdownSeparator />
                  <CountdownUnit value={timeLeft.hours} label={labels[1]} />
                  <CountdownSeparator />
                  <CountdownUnit value={timeLeft.minutes} label={labels[2]} />
                  <CountdownSeparator />
                  <CountdownUnit value={timeLeft.seconds} label={labels[3]} />
                </div>
              )}
            </div>

            {/* ---------------- Close (7625:11691 / 8632:8983) ------------ */}
            <DialogClose
              aria-label="Close"
              className="absolute right-[2.125rem] top-[1.75rem] flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-[0.5rem] border border-white/10 bg-white/10 transition-colors hover:bg-white/20 lg:right-[1.75rem] lg:top-[1.875rem] lg:border-0"
            >
              {/* eslint-disable-next-line @next/next/no-img-element -- exported glyph */}
              <img
                src="/promo-v1/close.svg"
                alt=""
                className="h-[3rem] w-[3rem]"
              />
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
